import { useRef, useState, useEffect } from 'react';
import { capture } from '../../utils/analytics';
import './PromoButton.css';

// Dedupe impressions per (promo, path) for the lifetime of the page load.
// PromoButton lives in a re-rendering ticker and mounts on several pages, so
// firing on every mount would inflate the CTR denominator. One impression per
// brand per route gives a stable "was the promo shown here" signal.
const seenImpressions = new Set();

function promoKey(brand, href) {
  return (brand || href || '').trim().toLowerCase() || 'unknown';
}

/**
 * Adaptive partner promo button — one component for every placement.
 *
 * Customisable:
 *   accent  — brand colour (drives border, brand text and badge via color-mix)
 *   shape   — 'round' (basic pages) | 'square' (terminal)
 *   prefix  — small lead-in shown only when there's room (e.g. "Trade on")
 *   brand   — partner name (e.g. "HYPERLIQUID")
 *   badge   — offer pill; pass a string, or { full, short, tiny } so it can
 *             shrink (e.g. "4% FEE DISCOUNT" → "4% OFF" → "4%")
 *   icon    — optional node shown before the text
 *
 * It auto-fits the space it's given. Hidden "ruler" copies of every richness
 * tier are measured, and on each resize the widest tier that fits the button's
 * available width is chosen — full → brand+short → brand+tiny → just "4%".
 * Deterministic (no shrink-cascade), so it never overflows, wraps or gets stuck.
 * Drop it into any flex row (give it room to flex-grow/shrink) and it adapts.
 */
const TIERS = [
  { prefix: true,  brand: true,  badge: 'full',  icon: true },
  { prefix: false, brand: true,  badge: 'short', icon: true },
  { prefix: false, brand: true,  badge: 'tiny',  icon: true },
  { prefix: false, brand: false, badge: 'tiny',  icon: true },
  { prefix: false, brand: false, badge: 'tiny',  icon: false },
];
const LAST = TIERS.length - 1;

function TierContent({ cfg, icon, prefix, brand, badgeText }) {
  return (
    <span className="promo-btn2-inner">
      {cfg.icon && icon && <span className="promo-btn2-icon">{icon}</span>}
      {((cfg.prefix && prefix) || (cfg.brand && brand)) && (
        <span className="promo-btn2-text">
          {cfg.prefix && prefix && <span className="promo-btn2-prefix">{prefix}</span>}
          {cfg.brand && brand && <span className="promo-btn2-brand">{brand}</span>}
        </span>
      )}
      {badgeText && <span className="promo-btn2-badge">{badgeText}</span>}
    </span>
  );
}

export default function PromoButton({ href, icon, prefix, brand, badge, accent = '#38bdf8', shape = 'round', className = '' }) {
  const outerRef = useRef(null);
  const rulerRefs = useRef([]);
  const [tier, setTier] = useState(0);

  const b = typeof badge === 'string' ? { full: badge, short: badge, tiny: badge } : (badge || {});
  const badgeOf = (cfg) => b[cfg.badge] || b.tiny || b.short || b.full;

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const pick = () => {
      raf = 0;
      // Only adapt where the button is given room to flex-grow (its width then
      // reflects its slot, independent of its own content). A rigid placement
      // (flex-grow: 0) just shows the full tier — a scrolling sibling yields room.
      const grow = parseFloat(getComputedStyle(outer).flexGrow) || 0;
      if (grow === 0) { setTier(0); return; }
      const avail = outer.offsetWidth;
      const widths = rulerRefs.current.map((r) => (r ? r.offsetWidth : Infinity));
      let chosen = LAST;
      for (let i = 0; i < widths.length; i += 1) {
        if (widths[i] <= avail + 2) { chosen = i; break; }
      }
      setTier(chosen);
    };
    const ro = new ResizeObserver(() => { if (!raf) raf = requestAnimationFrame(pick); });
    ro.observe(outer);
    pick();
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
    // re-measure if the offer text changes
  }, [brand, prefix, b.full, b.short, b.tiny]);

  const cfg = TIERS[Math.min(tier, LAST)];

  // One impression event per promo per route per page load. Gives a CTR
  // denominator for the partner promos, which previously had clicks but no
  // impression tracking (CTR was blind in the marketing digests).
  const promo = promoKey(brand, href);
  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const key = `${promo}|${path}`;
    if (seenImpressions.has(key)) return;
    seenImpressions.add(key);
    capture('promo_impression', { promo, brand, shape, page: path });
  }, [promo, brand, shape]);

  const handleClick = () => {
    capture('promo_click', {
      promo,
      brand,
      href,
      shape,
      page: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  };

  return (
    <a
      ref={outerRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`promo-btn2 promo-btn2--${shape}${className ? ` ${className}` : ''}`}
      style={{ '--promo-accent': accent }}
    >
      <TierContent cfg={cfg} icon={icon} prefix={prefix} brand={brand} badgeText={badgeOf(cfg)} />

      {/* hidden rulers — one per tier, measured to choose the widest that fits */}
      <span className="promo-btn2-rulers" aria-hidden="true">
        {TIERS.map((t, i) => (
          <span key={i} className="promo-btn2-ruler" ref={(el) => { rulerRefs.current[i] = el; }}>
            <TierContent cfg={t} icon={icon} prefix={prefix} brand={brand} badgeText={badgeOf(t)} />
          </span>
        ))}
      </span>
    </a>
  );
}
